/**
 * BingX REST API Functions
 *
 * REST endpoint helpers for BingX spot market data.
 * @see https://bingx-api.github.io/docs/
 */

import type {
  BingxDepthRequest,
  BingxDepthResponse,
  BingxKlinesRequest,
  BingxKlinesResponse,
  BingxSymbolsResponse,
  BingxTicker24hRequest,
  BingxTicker24hResponse,
  BingxTradesRequest,
  BingxTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://open-api.bingx.com';

/**
 * BingX REST API endpoints.
 * @see https://bingx-api.github.io/docs/#/
 */
export const bingxRestEndpoints = {
  symbols: `${BASE_URL}/openApi/spot/v1/common/symbols`,
  ticker24h: `${BASE_URL}/openApi/spot/v1/ticker/24hr`,
  klines: `${BASE_URL}/openApi/spot/v2/market/kline`,
  trades: `${BASE_URL}/openApi/spot/v1/market/trades`,
  depth: `${BASE_URL}/openApi/spot/v1/market/depth`,
} as const;

/**
 * Formats base and quote currency into BingX symbol format (e.g., BTC-USDT).
 */
export const formatBingxSymbol = (base: string, quote: string): string => {
  return `${base.toUpperCase()}-${quote.toUpperCase()}`;
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#24hr%20Ticker%20Price%20Change%20Statistics
 */
export const getBingxTicker24h = async (
  params: BingxTicker24hRequest,
  options?: RestRequestOptions,
): Promise<BingxTicker24hResponse> => {
  const response = await get<BingxTicker24hResponse, BingxTicker24hRequest & { timestamp: number }>(
    {
      url: bingxRestEndpoints.ticker24h,
      params: { ...params, timestamp: Date.now() },
      options,
    },
  );

  return response.data;
};

/**
 * Fetches all trading symbols metadata from BingX.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#Query%20Symbols
 */
export const getBingxSymbols = async (
  options?: RestRequestOptions,
): Promise<BingxSymbolsResponse> => {
  const response = await get<BingxSymbolsResponse>({
    url: bingxRestEndpoints.symbols,
    params: { timestamp: Date.now() },
    options,
  });

  return response.data;
};

/**
 * Fetches historical klines (candlesticks) from BingX.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#Kline/Candlestick%20Data
 */
export const getBingxKlines = async (
  params: BingxKlinesRequest,
  options?: RestRequestOptions,
): Promise<BingxKlinesResponse> => {
  const response = await get<BingxKlinesResponse>({
    url: bingxRestEndpoints.klines,
    params: { ...params, timestamp: Date.now() },
    options,
  });

  return response.data;
};

/**
 * Fetches recent public trades for a symbol.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#Recent%20Trades%20List
 */
export const getBingxTrades = async (
  params: BingxTradesRequest,
  options?: RestRequestOptions,
): Promise<BingxTradesResponse> => {
  const response = await get<BingxTradesResponse>({
    url: bingxRestEndpoints.trades,
    params: { ...params, timestamp: Date.now() },
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth (snapshot) for a symbol.
 * @see https://bingx-api.github.io/docs/#/en-us/spot/market-api.html#Order%20Book
 */
export const getBingxDepth = async (
  params: BingxDepthRequest,
  options?: RestRequestOptions,
): Promise<BingxDepthResponse> => {
  const response = await get<BingxDepthResponse>({
    url: bingxRestEndpoints.depth,
    params: { ...params, timestamp: Date.now() },
    options,
  });

  return response.data;
};
